import { useLayoutEffect, useRef, type RefObject } from 'react';
import { createAsciiReveal, presets } from 'ascii-reveal';
import { ARABIC_LETTERS } from '@/lib/ascii-dither/glyphs';
import { useDitherLoading } from './dither-loading-context';

const ARABIC_DITHER = { ...presets.dither, scramble: ARABIC_LETTERS };

/** Keep the original fast dither reveal, once per first successful read. */
export function useDitherReveal(root: RefObject<HTMLElement | null>, id: string, status: string) {
  const scope = useDitherLoading();
  const previous = useRef(status);
  useLayoutEffect(() => {
    const firstRead = previous.current === 'loading' && status === 'ready' && scope && !scope.has(id);
    previous.current = status;
    if (['ready', 'empty', 'stale'].includes(status)) scope?.add(id);
    const node = root.current;
    if (typeof window.matchMedia !== 'function' || typeof CanvasRenderingContext2D === 'undefined') return;
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!firstRead || !node || preference.matches || document.hidden || node.contains(document.activeElement)) return;
    const rect = node.getBoundingClientRect();
    if (!rect.width || !rect.height || rect.bottom <= 0 || rect.top >= innerHeight || node.closest('[hidden],[inert]')) return;
    // Let the browser keep joined Arabic and inline artwork intact. Decorative Arabic lives in the scan.
    const skipped = new Map<Element, string | null>();
    const skip = (el: Element) => { if (skipped.has(el)) return; skipped.set(el, el.getAttribute('data-ascii')); el.setAttribute('data-ascii', 'skip'); };
    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) if (/[\u0600-\u06ff]/u.test(walker.currentNode.textContent || '') && walker.currentNode.parentElement) skip(walker.currentNode.parentElement);
    node.querySelectorAll('svg').forEach(skip);
    const position = node.style.position;
    const controller = createAsciiReveal(node, { preset: ARABIC_DITHER, duration: 'fast', lazy: false, fallback: true });
    node.dataset.ditherRevealing = '';
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      controller.destroy();
      delete node.dataset.ditherRevealing;
      for (const [el, value] of skipped) { if (value === null) el.removeAttribute('data-ascii'); else el.setAttribute('data-ascii', value); }
      if (position) node.style.position = position;
      else node.style.removeProperty('position');
      node.removeEventListener('focusin', finish);
      node.removeEventListener('pointerdown', finish, true);
      window.removeEventListener('resize', finish);
      document.removeEventListener('visibilitychange', finish);
      preference.removeEventListener('change', finish);
    };
    node.addEventListener('focusin', finish);
    node.addEventListener('pointerdown', finish, true);
    window.addEventListener('resize', finish);
    document.addEventListener('visibilitychange', finish);
    preference.addEventListener('change', finish);
    void controller.reveal({ within: node }).then(finish, finish);
    return finish;
  }, [root, id, status, scope]);
}
